import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Box,
  Chip,
  Stack,
  Avatar,
  Typography,
  Rating,
  IconButton,
  Tooltip
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import StarIcon from "@mui/icons-material/Star";
import CommentIcon from "@mui/icons-material/Comment";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { API_URL, popupTimer } from "../../consts";
import { toggleLike } from "../../services/likeService";

// Common components
import StatisticsDialog from "./StatisticsDialog";
import ChipList from "./ChipList";
import PublisherComment from "../Home/PublisherComment";
import HomeCardActionsMenu from "../Home/HomeCardActionsMenu";

export const showUnauthorizedErrorPopup = (dispatch) => {
  dispatch({
    type: "SET_POPUP",
    payload: {
      text: "You must Login to Perform this Action",
      type: "error",
    },
  });
  setTimeout(() => {
    dispatch({
      type: "SET_POPUP",
      payload: {
        text: "",
        type: "",
      },
    });
  }, popupTimer);
};

const CardItem = ({
  // Common props
  data,
  searhTags,
  selected,
  isSelected,
  displayStars = true,
  isBookmark = false,
  isFeedback = false,
  applyMargin = false,
  
  // Action props
  onRemoveFromSpaceClick,
  onAddToSpaceClick,
  onRemove,
  onDelete, // Add delete handler prop
  onRateSuccessfully,
  onSelect,
  
  // Display control props
  displayRemoveFromSpace = false,
  displayAddToSpace = false,
  showRemove = false,
  
  // Custom components
  CustomMedia,
  CustomHeader,
  CustomBody,
  CustomActions,
  
  // Callbacks
  onCardClick,
  onMenuAction,
  onOpenComments,
  showSpacePopup: externalShowSpacePopup,
  hideUserInfo = false,
  isCommunity = false, // Add isCommunity prop
  onVisibilityUpdate, // Callback when visibility is updated
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authorized = useSelector((state) => state.authorized);
  const currentUser = useSelector((state) => state.user);

  // State
  const [showRateModal, setShowRateModal] = useState(false);
  const [openStatsModal, setOpenStatsModal] = useState(false);
  const [rateId, setRateId] = useState(null);
  const [openSpaceModal, setOpenSpaceModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(data.likes || data.likes_count || 0);
  const [isLiking, setIsLiking] = useState(false);
  const openMenu = Boolean(anchorEl);

  // Sync like count when data changes
  useEffect(() => {
    setLikeCount(data.likes || data.likes_count || 0);
  }, [data.likes, data.likes_count]);

  // Handlers
  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authorized || isLiking || !data.rating_id) return;
    
    setIsLiking(true);
    try {
      const response = await toggleLike({ rating_id: data.rating_id });
      setLikeCount(response.like_count || 0);
      setIsLiked(!isLiked);
    } catch (error) {
      if (error.message === "UNAUTHORIZED") {
        dispatch({ type: "SET_UNAUTHORIZED" });
      } else {
      }
    } finally {
      setIsLiking(false);
    }
  };

  const onRatingClick = () => {
    setOpenStatsModal(true);
  };

  const showSpacePopup = (objectId, itemData = null) => {
    if (externalShowSpacePopup) {
      externalShowSpacePopup(objectId, itemData || data);
    } else {
      if (authorized) {
        setRateId(objectId);
        setOpenSpaceModal(true);
      } else {
        showUnauthorizedErrorPopup(dispatch);
      }
    }
  };

  const showRatePopup = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (authorized) {
      setShowRateModal(true);
    } else {
      showUnauthorizedErrorPopup(dispatch);
    }
  };

  const handleCardClick = (e) => {
    // Don't trigger card click if clicking on menu button or its children
    if (e.target.closest('#card-menu-button')) {
      return;
    }

    // Don't trigger card click if clicking on profile image or name
    if (e.target.closest('[data-profile-click]') || 
        e.target.closest('.MuiAvatar-root') ||
        (e.target.tagName === 'TYPography' && e.target.closest('[data-profile-click]'))) {
      return;
    }
    
    if (onCardClick) {
      onCardClick(e, data);
    }
  };

  return (
    <>
      <Card
        className="cardItem"
        sx={{
          border: selected || isSelected 
            ? "2px solid #6366f1" 
            : "1px solid rgba(226,232,240,0.6)",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          boxShadow: selected || isSelected 
            ? "0 8px 32px rgba(99,102,241,0.2), 0 0 0 1px rgba(99,102,241,0.1)" 
            : "0 4px 16px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
          cursor: "pointer",
          height: "100%",
          width: "100%",
          borderRadius: "16px",
          overflow: "visible",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-8px)",
            boxShadow: "0 12px 40px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.1)",
            borderColor: "rgba(99,102,241,0.3)",
          },
          "&:active": {
            transform: "translateY(-4px)",
          }
        }}
        onClick={handleCardClick}
      >
        {/* Header Section - Before Media */}
        {CustomHeader ? (
          <CustomHeader
            data={data}
            isFeedback={isFeedback}
            displayStars={displayStars}
            isBookmark={isBookmark}
            onRatingClick={onRatingClick}
            showRatePopup={showRatePopup}
            navigate={navigate}
            isCommunity={isCommunity}
            onVisibilityUpdate={onVisibilityUpdate}
          />
        ) : (
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (data?.user_name) {
                    navigate(`/user/${data.user_name}`);
                  } else if (data?.user_id) {
                    navigate(`/user/${data.user_id}`);
                  }
                }}
                data-profile-click="true"
              >
                <Avatar
                  alt="Profile Picture"
                  src={data.profile_image_url}
                  sx={{ maxHeight: '30px', maxWidth: '30px', marginRight: '10px' }}
                />
                <Typography>{data.display_name || (data.first_name && data.last_name ? `${data.first_name} ${data.last_name}` : data.first_name || data.last_name || data.user_name || "Anonymous User")}</Typography>
              </Box>
              {isBookmark && displayStars && data.average_stars > 0 && (
                <Rating
                  value={data.average_stars}
                  readOnly
                  emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                  sx={{ color: "rgba(255, 204, 72, 1)" }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Media Section */}
        <Box sx={{ position: 'relative' }}>
          {CustomMedia ? (
            <CustomMedia data={data} hideUserInfo={true} />
          ) : (
            <Box
              sx={{
                width: '100%',
                minHeight: '45%',
                maxHeight: '45%',
                background: !data.img_url 
                  ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
                  : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: '32px',
                fontWeight: 'bold',
                borderRadius: '0',
                overflow: 'hidden',
                position: 'relative',
                '&::after': !data.img_url ? {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                } : {}
              }}
            >
              {data.img_url ? (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '0',
                    background: 'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(255,255,255,0.8) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={data.img_url}
                    alt={data.name || "Card image"}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                    }}
                  />
                </div>
              ) : (
                data.name && (
                  <Typography variant="h4" component="h4">
                    {data.name[0]}
                  </Typography>
                )
              )}
            </Box>
          )}

        </Box>

        {/* Statistics Dialog */}
        <StatisticsDialog
          data={data?.stars}
          total={data?.stars && Object.values(data.stars).reduce((sum, a) => sum + a, 0)}
          handleClose={() => setOpenStatsModal(false)}
          open={openStatsModal}
        />

        {/* Content Section */}
        <CardContent sx={{ 
          position: 'relative', 
          paddingTop: '0px !important',
          p: 2,
          pb: 1.5,
          background: 'rgba(255,255,255,0.6)',
          backdropFilter: 'blur(15px)',
          WebkitBackdropFilter: 'blur(15px)',
          borderTop: '1px solid rgba(226,232,240,0.3)'
        }}>
          {CustomBody ? (
            <CustomBody data={data} searhTags={searhTags} onMenuAction={onMenuAction} />
          ) : (
            <>
              {data.name && (
                <Typography 
                  variant="h5" 
                  component="h4" 
                  sx={{ 
                    mb: 2,
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    color: 'rgba(30,41,59,0.9)',
                    lineHeight: 1.3
                  }}
                >
                  {data.name}
                </Typography>
              )}
              {data.description && (
                <Box sx={{ mb: 2 }}>
                  <PublisherComment comment={data.description} onClickDisabled={true} />
                </Box>
              )}
              {/* Tags Section - Always show heading */}
              <Box sx={{ mt: 2 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(99,102,241,0.8)',
                    fontWeight: 600,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 1,
                    display: 'block'
                  }}
                >
                  Tags
                </Typography>
                {data.keywords && data.keywords.length > 0 && (
                  <ChipList list={data.keywords} highlightList={searhTags} AllowChipOverflow={true} />
                )}
              </Box>

              {/* Collections Section - Always show heading */}
              <Box sx={{ mt: 2 }}>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'rgba(99,102,241,0.8)',
                    fontWeight: 600,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 1,
                    display: 'block'
                  }}
                >
                  Collections
                </Typography>
                {(data.spaces_list || data.spaces || data.collections) && ((data.spaces_list && data.spaces_list.length > 0) || (data.spaces && data.spaces.length > 0) || (data.collections && data.collections.length > 0)) && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      flexWrap: 'wrap',
                      gap: 0.5
                    }}
                  >
                    {(data.spaces_list || data.spaces || data.collections || []).map((space, index) => (
                      <Chip
                        key={space.id || space._id || index}
                        label={space.name || space.title}
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (onMenuAction) onMenuAction('spaceClick', space);
                        }}
                        sx={{
                          background: 'linear-gradient(135deg, rgba(139,92,246,0.1) 0%, rgba(168,85,247,0.1) 100%)',
                          color: 'rgba(139,92,246,0.8)',
                          border: '1px solid rgba(139,92,246,0.2)',
                          borderRadius: '8px',
                          fontSize: '11px',
                          height: '24px',
                          fontWeight: 500,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(168,85,247,0.15) 100%)',
                            borderColor: 'rgba(139,92,246,0.3)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 2px 8px rgba(139,92,246,0.2)',
                          },
                          cursor: 'pointer',
                          '& .MuiChip-label': {
                            px: 1.5
                          }
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </>
          )}
        </CardContent>

        {/* Modern Menu Button */}
        <Box
          sx={{
            position: "absolute",
            top: "0px",
            right: "0px",
            background: "#ffffff",
            border: "none",
            borderRadius: "0 0 0 16px",
            width: "44px",
            height: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "none",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            zIndex: 10,
            pointerEvents: "none",
            "&:hover": {
              background: "#f8f9fa",
              transform: "none",
            },
          }}
        >
          <Button
            id="card-menu-button"
            aria-controls={openMenu ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={openMenu ? "true" : undefined}
            onClick={(e) => {
              handleClick(e);
            }}
            sx={{
              minWidth: "unset",
              color: "rgba(99,102,241,0.8)",
              p: 0,
              borderRadius: "0 0 0 16px",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              cursor: "pointer",
              pointerEvents: "auto",
              width: "100%",
              height: "100%",
              "&:hover": { 
                backgroundColor: "transparent",
                color: "rgba(99,102,241,1)",
              },
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '2px',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Box sx={{ 
                width: '4px', 
                height: '4px', 
                borderRadius: '50%', 
                background: 'currentColor',
                transition: 'all 0.2s ease'
              }} />
              <Box sx={{ 
                width: '4px', 
                height: '4px', 
                borderRadius: '50%', 
                background: 'currentColor',
                transition: 'all 0.2s ease'
              }} />
              <Box sx={{ 
                width: '4px', 
                height: '4px', 
                borderRadius: '50%', 
                background: 'currentColor',
                transition: 'all 0.2s ease'
              }} />
            </Box>
          </Button>
        </Box>

        {/* Actions Section */}
        {isBookmark && (
          <CardActions 
            disableSpacing 
            sx={{ 
              flexWrap: "wrap", 
              position: "relative",
              p: 3,
              pt: 1,
              background: "rgba(255,255,255,0.4)",
              backdropFilter: "blur(15px)",
              WebkitBackdropFilter: "blur(15px)",
              borderTop: "1px solid rgba(226,232,240,0.2)",
              borderRadius: "0 0 16px 16px"
            }}
          >
            {CustomActions ? (
              <CustomActions
                data={data}
                isFeedback={isFeedback}
                showRatePopup={showRatePopup}
                applyMargin={applyMargin}
              />
            ) : (
              <>
                {isFeedback && (
                  <Button
                    sx={{
                      background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                      color: "white",
                      fontWeight: "600",
                      borderRadius: "12px",
                      textTransform: "none",
                      px: 3,
                      py: 1,
                      fontSize: "0.875rem",
                      border: "1px solid rgba(255,255,255,0.2)",
                      boxShadow: "0 2px 8px rgba(99,102,241,0.3)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #5855eb 0%, #7c3aed 100%)",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(99,102,241,0.4)",
                      },
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                    className="button-rate"
                    onClick={showRatePopup}
                  >
                    🔄 Replay
                  </Button>
                )}
                <Box sx={{ marginLeft: applyMargin ? { sm: "0px", md: "auto" } : "auto" }} />
              </>
            )}
          </CardActions>
        )}

        {/* Menu Popup */}
        <HomeCardActionsMenu
          anchorEl={anchorEl}
          openMenu={openMenu}
          handleClose={handleClose}
          data={data}
          showSpacePopup={showSpacePopup}
          displayRemoveFromSpace={displayRemoveFromSpace}
          onRemoveFromSpaceClick={onRemoveFromSpaceClick}
          displayAddToSpace={displayAddToSpace}
          onAddToSpaceClick={onAddToSpaceClick}
          showRemove={showRemove}
          onRemove={onRemove}
          onDelete={onDelete}
          isFeedback={isFeedback}
          currentUser={currentUser}
        />

        {/* Like and Comment Buttons Overlay - Bottom Right Corner */}
        <Box
          sx={{
            position: "absolute",
            bottom: "12px",
            right: "12px",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Tooltip title={isLiked ? "Unlike" : "Like"} arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={handleLike}
                disabled={isLiking || !authorized}
                sx={{
                  color: isLiked ? "#e91e63" : "#6366f1",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    color: isLiked ? "#c2185b" : "#5855eb",
                    transform: "scale(1.1)",
                  },
                }}
              >
                {isLiked ? (
                  <FavoriteIcon sx={{ fontSize: "24px" }} />
                ) : (
                  <FavoriteBorderIcon sx={{ fontSize: "24px" }} />
                )}
              </IconButton>
              {likeCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6366f1",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    minWidth: "20px",
                  }}
                >
                  {likeCount}
                </Typography>
              )}
            </Box>
          </Tooltip>
          {onOpenComments && (
            <Tooltip title="Other reviews" arrow>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenComments(e);
                }}
                sx={{
                  color: "#6366f1",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    color: "#5855eb",
                    transform: "scale(1.1)",
                  },
                }}
              >
                <CommentIcon sx={{ fontSize: "24px" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Card>
    </>
  );
};

export default CardItem; 
